import {
  ACTIVITY_SUBTYPES,
  IActivityItem,
} from "@rentiohq/shared-frontend/dist/types/activity.types";
import {
  formatDate,
  formatDateAndTime,
} from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatCurrency } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import { getPaymentOrders } from "@rentiohq/shared-frontend/dist/utils/paymentRequest.utils";
import {
  Button,
  MainCard,
  Box,
  Table,
  Typography,
  SpinningLoader,
} from "@rentiohq/web-shared-next/dist/ui-components";
import { TDataRow } from "@rentiohq/web-shared-next/dist/ui-components/Table/Table";
import {
  EButtonColors,
  EButtonVariants,
} from "@rentiohq/web-shared-next/dist/utils/types/button";
import { getDirection } from "scenes/PaymentsOverview/PaymentHistory/PaymentHistory.utils";
import { createTitle, getPeriod } from "utils/payment";

interface IProps {
  activities: IActivityItem[];
  isLoading: boolean;
  fetchMore: () => void;
  activitiesCount?: number;
}

export const PaymentHistoryTable = ({
  activities,
  isLoading,
  fetchMore,
  activitiesCount = 0,
}: IProps) => {
  const columns = [
    {
      columnTitle: "payment.payment_order.due_date.heading",
    },
    {
      columnTitle: "payment_history.heading.amount",
    },
    {
      columnTitle: "payment_history.heading.description",
    },
    {
      columnTitle: "payment_history.heading.period",
    },
    {
      columnTitle: "payment_history.heading.property",
    },
    {
      columnTitle: "payment_history.heading.due_date",
    },
    {
      columnTitle: "payment_history.heading.method",
    },
  ];

  const getRows = () => {
    if (activities?.length === 0) {
      return [];
    }

    const getAmount = (entity: IActivityItem) => {
      let amount = 0;

      switch (entity.subtype) {
        case ACTIVITY_SUBTYPES.PAYMENT.REQUEST_MANUALLY_PAID:
          if (entity.amount) {
            ({ amount } = entity);
          }

          break;

        case ACTIVITY_SUBTYPES.PAYMENT.PAYIN_DISPUTED:
          if (entity.paymentRequest) {
            amount = entity.paymentRequest.originalAmount;
          }

          break;

        default:
          if (entity.paymentTransaction) {
            ({ amount } = entity.paymentTransaction);
            break;
          }

          if (entity.paymentRequest) {
            amount = entity.paymentRequest.originalAmount;
            break;
          }

          break;
      }

      const direction = getDirection(entity);

      return formatCurrency(direction === "in" ? amount : -amount);
    };

    const rows = activities.map(activity => {
      const { id, datetime, property, paymentRequest } = activity;

      return {
        id,
        content: [
          formatDateAndTime(datetime),
          getAmount(activity),
          paymentRequest
            ? createTitle({
                paymentOrders: getPaymentOrders(paymentRequest),
              })
            : "-",
          paymentRequest ? getPeriod(paymentRequest) : "-",
          property?.id ? property.propertyAddress?.full : "-",
          paymentRequest?.dueDateAt
            ? formatDate(paymentRequest.dueDateAt)
            : "-",
          paymentRequest?.paymentMethod
            ? getLocalizedText(
                `payment_request.method.${paymentRequest?.paymentMethod}`.toLowerCase(),
              )
            : "-",
        ],
      };
    });
    return rows;
  };

  const renderTable = () => {
    return <Table columns={columns} dataRows={getRows() as TDataRow[]} />;
  };

  const renderLoadMore = () => {
    if (activitiesCount <= activities.length) {
      return null;
    }

    if (isLoading) {
      return null;
    }

    return (
      <Button
        onClick={fetchMore}
        variant={EButtonVariants.Text}
        color={EButtonColors.Success}
      >
        {getLocalizedText("system.load_more")}
      </Button>
    );
  };

  return (
    <Box>
      <Typography variant="h6" mb={1}>
        {getLocalizedText(
          "payments.payment_requests.history.heading",
        ).toUpperCase()}
      </Typography>
      <MainCard content={false}>
        {renderTable()}
        {renderLoadMore()}
        {isLoading && <SpinningLoader />}
      </MainCard>
    </Box>
  );
};
